import { useAxios } from '@healthblocks-io/core'
import { useCallback, useState } from 'react'

/**
 * Turns the "call me if something comes free" request on or off for an
 * appointment that already exists.
 *
 * Unlike cancelling, which tells Synops and then patches Healthblocks from
 * here, this is a single call: the api talks to Synops and writes the
 * appointment in the same request, so the two can never disagree halfway.
 *
 * Deliberately not built on `useAxiosAsync`: that helper catches the failure
 * and reports it through state, resolving as though nothing went wrong. The
 * switch is moved before the request is sent, so the caller has to be able to
 * await the outcome and put it back.
 */
function useToggleWaitlist() {
  const axios = useAxios()
  const [isLoading, setIsLoading] = useState(false)

  const toggleWaitlist = useCallback(
    async (appointmentId: number, waitlisted: boolean): Promise<void> => {
      setIsLoading(true)

      try {
        // The response carries the stored row, whose shape is the api's rather
        // than this app's. The caller holds its own optimistic copy over the
        // timeline instead, so nothing here needs reading back.
        await axios.patch(`/appointment/${appointmentId}/waitlist`, { waitlisted })
      } finally {
        setIsLoading(false)
      }
    },
    [axios],
  )

  return { toggleWaitlist, isLoading }
}

export { useToggleWaitlist }
